<template>
  <MainLayout :initialDrawerExpanded="false" contentClass="pa-md-4" :has-sidebar="false">
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">
        <v-row>
          <v-col cols="12" md="10" offset-md="1">

            <div v-if="loading" class="d-flex justify-center py-12">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <v-alert v-else-if="error" type="error" class="mb-4">{{ error }}</v-alert>

            <template v-else-if="org">

              <!-- Header -->
              <div class="d-flex align-center flex-wrap mb-5" style="gap: 12px">
                <v-avatar color="primary lighten-4" size="48">
                  <v-img v-if="org.icon" :src="org.icon" />
                  <v-icon v-else color="primary">mdi-domain</v-icon>
                </v-avatar>
                <div class="subtitle-1 font-weight-medium">{{ org.name }}</div>
              </div>

              <v-row align="start">
                <!-- Profile card (read-only) -->
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                    <v-card-title class="subtitle-1 font-weight-medium pb-1">
                      <v-icon left small>mdi-domain</v-icon>
                      Profil
                    </v-card-title>
                    <v-card-text style="display: grid; gap: 10px">
                      <div>
                        <div class="caption grey--text">Nom</div>
                        <div class="body-2">{{ org.name }}</div>
                      </div>
                      <div>
                        <div class="caption grey--text">Créée le</div>
                        <div class="body-2">{{ formatDate(org.created_at) }}</div>
                      </div>
                      <div class="caption grey--text">
                        Nom / icône / bannière modifiables uniquement depuis Django admin.
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Allowed engines card -->
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                    <v-card-title class="subtitle-1 font-weight-medium pb-1">
                      <v-icon left small>mdi-robot-outline</v-icon>
                      Moteurs autorisés
                    </v-card-title>
                    <v-card-text>
                      <v-autocomplete
                        v-model="allowedEngineIds"
                        :items="engineChoices"
                        multiple chips small-chips deletable-chips
                        outlined dense
                        label="Moteurs (vide = aucune restriction)"
                        :loading="engineSaving"
                      />
                      <v-btn color="primary" elevation="0" small :loading="engineSaving" @click="saveAllowedEngines">
                        Enregistrer
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Teams table -->
              <v-card elevation="0" outlined>
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-account-group-outline</v-icon>
                  Équipes
                </v-card-title>
                <v-data-table
                  :headers="teamHeaders"
                  :items="org.teams"
                  sort-by="name"
                  class="elevation-0"
                  @click:row="item => $router.push({ name: 'admin_team_detail', params: { id: item.id } })"
                  style="cursor: pointer"
                >
                  <template v-slot:[`item.name`]="{ item }">
                    <a class="font-weight-medium text-decoration-none" style="cursor:pointer"
                       @click.stop="$router.push({ name: 'admin_team_detail', params: { id: item.id } })">
                      {{ item.name }}
                    </a>
                  </template>
                  <template v-slot:[`item.created_at`]="{ item }">{{ formatDate(item.created_at) }}</template>
                </v-data-table>
              </v-card>

            </template>

            <v-row v-else>
              <v-col cols="12" md="8" offset-md="2">
                <v-alert type="error" outlined>Organisation introuvable.</v-alert>
              </v-col>
            </v-row>

          </v-col>
        </v-row>
      </v-container>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import { fetchAdminOrganisation, updateAdminOrganisation, fetchAdminEngines } from '@/services/adminApi'

export default {
  name: 'AdminOrganisationDetailView',
  components: { PageBack, MainLayout },

  data() {
    return {
      loading: false,
      error: null,
      org: null,
      allowedEngineIds: [],
      engineChoices: [],
      engineSaving: false,

      teamHeaders: [
        { text: 'Nom', value: 'name' },
        { text: 'Membres', value: 'member_count', align: 'right' },
        { text: 'Créée', value: 'created_at' },
      ],
    }
  },

  computed: {
    orgId() {
      return this.$route.params.id
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const [org, engines] = await Promise.all([
          fetchAdminOrganisation(this.orgId),
          fetchAdminEngines(),
        ])
        this.org = org
        this.allowedEngineIds = org.allowed_engines.map(e => e.id)
        this.engineChoices = engines.map(e => ({ text: e.name, value: e.id }))
      } catch {
        this.error = 'Impossible de charger cette organisation.'
      } finally {
        this.loading = false
      }
    },

    async saveAllowedEngines() {
      this.engineSaving = true
      try {
        this.org = await updateAdminOrganisation(this.orgId, { allowed_engines: this.allowedEngineIds })
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la mise à jour.' })
      } finally {
        this.engineSaving = false
      }
    },

    formatDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
    },
  },
}
</script>
